import {
  CACHE_HEADER_ENABLED,
  CACHE_HEADER_STALE_TIME,
  listenForCacheUpdate,
} from "@sinch/cache-helpers";
import {
  getQueryClient,
  getQueryKey,
} from "@sinch/mfe-helper-react/lib/cacheHelpers";
import { useCallback, useEffect, useState } from "react";
import { apiUrls, formatters } from "..";
import { useLoginToken } from "../../hooks";
import type { ShellUser } from "../types";
type UpdateUser = (requestOption?: RequestInit) => Promise<ShellUser>;

export const useFetchUser = (
  initialData?: ShellUser,
  enabled = true
): [ShellUser | undefined, UpdateUser] => {
  const token = useLoginToken()?.token;
  const [user, setUser] = useState<ShellUser | undefined>(initialData);

  const handleUserResponse = async (res: Response) => {
    const platformUser = await res.json();
    const shellUser = formatters.toShellUser(platformUser);
    setUser(shellUser);
    return shellUser;
  };

  const fetchUser = useCallback<UpdateUser>(async () => {
    const userResponse = await window.fetch(apiUrls.userUrl, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
        [CACHE_HEADER_ENABLED]: "true",
        [CACHE_HEADER_STALE_TIME]: "10000",
      },
    });

    const data = await userResponse.clone().json();

    // Fix for: https://sinch.slack.com/archives/C04104TGPV3/p1719866483063509
    // remove when https://gitlab.com/sinch/sinch-projects/applications/teams/campaign-creation/campaign-creation/-/merge_requests/1162/diffs is merged
    getQueryClient().setQueryData(getQueryKey(new URL(apiUrls.userUrl)), data);

    return handleUserResponse(userResponse);
  }, [token]);

  useEffect(() => {
    if (token && enabled) {
      fetchUser();
    }
  }, [enabled, fetchUser, token]);

  useEffect(
    () => listenForCacheUpdate(new URL(apiUrls.userUrl), handleUserResponse),
    []
  );

  return [user, fetchUser];
};
